import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Typography,
  Paper,
  styled,
} from '@mui/material'
import React from 'react'

type RadioCardOption = {
  title: string | React.ReactNode
  description?: string | React.ReactNode
  value: any
}

export type RadioCardProps = Omit<RadioGroupProps, 'onChange'> & {
  label?: string | React.ReactNode
  labelSize?: 'small' | 'medium' | 'large'
  caption?: string
  optional?: boolean
  error?: boolean | string
  optionalTextOverride?: string
  options: RadioCardOption[]
  'data-testid'?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

// Styled component for the card
const StyledCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'hasError',
})<{ isSelected: boolean; hasError: boolean }>(
  ({ theme, isSelected, hasError }) => {
    let border = '2px solid transparent'
    if (hasError) {
      border = `2px solid ${theme.palette.error.main}`
    } else if (isSelected) {
      border = `2px solid ${theme.palette.primary.main}`
    }
    return {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px',
      width: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
      borderRadius: '16px',
      border,
      transition: theme.transitions.create(['box-shadow', 'border-color']),
      '&:hover': {
        boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)',
      },
      boxSizing: 'border-box',
    }
  }
)

const RadioCard = (props: RadioCardProps) => {
  const {
    options,
    label,
    labelSize,
    caption,
    optional,
    optionalTextOverride,
    error,
    'data-testid': dataTestId,
    value,
    onChange,
    ...radioGroupProps
  } = props

  const optionalText = optionalTextOverride ?? '(optional)'
  const hasError = !!error

  return (
    <FormControl
      error={hasError}
      sx={{ width: '100%', gap: '12px' }}
      data-testid={dataTestId}
      data-type="radio-card"
      data-options={options.map((option) => option.value).join(',')}
    >
      {label && (
        <FormLabel sx={{ color: hasError ? 'error.main' : 'gray8.main' }}>
          <Typography variant={labelSize === 'small' ? 'p2' : 'h4'}>
            {label}
            <Typography
              component="span"
              variant={labelSize === 'small' ? 'p2' : 'h4'}
              color="gray5.main"
              display="inline"
            >
              {optional && ` ${optionalText}`}
            </Typography>
          </Typography>
        </FormLabel>
      )}
      {caption && (
        <FormLabel sx={{ color: hasError ? 'error.main' : 'gray8.main' }}>
          <Typography
            component="span"
            variant={labelSize === 'small' ? 'p3' : 'h4'}
            color="gray5.main"
          >
            {caption}
          </Typography>
        </FormLabel>
      )}
      <RadioGroup
        value={value}
        onChange={onChange}
        sx={{ width: '100%' }}
        {...radioGroupProps}
      >
        {options.map((option) => {
          const { title, description, value: optionValue } = option
          const isSelected = value === optionValue

          return (
            <FormControlLabel
              key={optionValue}
              value={optionValue}
              control={<Radio sx={{ display: 'none' }} />}
              sx={{
                margin: 0,
                width: '100%',
                marginBottom: 2,

                '& .MuiFormControlLabel-label': {
                  width: '100%', // makes sure that each card takes up full width
                },
              }}
              data-testid={`${dataTestId}-${optionValue}`}
              label={
                <StyledCard isSelected={isSelected} hasError={hasError}>
                  <Typography
                    variant="cta"
                    color={hasError ? 'error.main' : 'gray8.main'}
                    gutterBottom={!!description}
                  >
                    {title}
                  </Typography>
                  {description && (
                    <Typography
                      variant="p2"
                      color={hasError ? 'error.main' : 'gray8.main'}
                      sx={{ whiteSpace: 'pre-line' }} // allows for \n line separations in description
                    >
                      {description}
                    </Typography>
                  )}
                </StyledCard>
              }
            />
          )
        })}
      </RadioGroup>
      {error && typeof error === 'string' && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}
    </FormControl>
  )
}

RadioCard.defaultProps = {
  label: '',
  labelSize: 'large',
  optional: false,
  optionalTextOverride: undefined,
  error: false,
  'data-testid': undefined,
}

export default RadioCard
